import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getDashboardMetrics, type DashboardMetrics } from "@/lib/processingMetrics";
import {
  getCompanyCostReport,
  getUserActivityReport,
  listOrganizations,
  currentMonthKey,
  lastMonthKeys,
  monthLabel,
  type CompanyCostReport,
  type UserActivityRow,
  type CostPeriod,
  type OrganizationOption,
} from "@/lib/dashboardCosts";
import CompanyCostTable from "@/components/dashboard/CompanyCostTable";
import UserActivityTable from "@/components/dashboard/UserActivityTable";
import DevelopmentCostsCard from "@/components/dashboard/DevelopmentCostsCard";

import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings, Coins, Users } from "lucide-react";
import BackButton from "@/components/BackButton";

const formatNumber = (n: number) => new Intl.NumberFormat("es-ES").format(n);

const Dashboard = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [report, setReport] = useState<CompanyCostReport | null>(null);
  const [userReport, setUserReport] = useState<{ rows: UserActivityRow[]; total: number } | null>(null);
  const [period, setPeriod] = useState<CostPeriod>(currentMonthKey());
  const [organizationId, setOrganizationId] = useState<string>("all");
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
  const [loading, setLoading] = useState(true);

  const loadOrganizations = useCallback(async () => {
    setOrganizations(await listOrganizations());
  }, []);

  const loadReport = useCallback(async () => {
    if (!isAdmin) return;
    const [r, u] = await Promise.all([
      getCompanyCostReport(period, organizationId),
      getUserActivityReport(period),
    ]);
    setReport(r);
    setUserReport(u);
  }, [isAdmin, period, organizationId]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      if (isAdmin) {
        const [r, u] = await Promise.all([
          getCompanyCostReport(period, organizationId),
          getUserActivityReport(period),
        ]);
        if (!cancelled) {
          setReport(r);
          setUserReport(u);
        }
      } else {
        const m = await getDashboardMetrics(user.id, false);
        if (!cancelled) setMetrics(m);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, isAdmin, period, organizationId]);

  useEffect(() => {
    if (isAdmin) loadOrganizations();
  }, [isAdmin, loadOrganizations]);

  const months = lastMonthKeys(12);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-6 py-4">
          <div>
            <h1
              className="text-2xl font-semibold tracking-tight text-primary"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Panel de control
            </h1>
            <p className="text-sm text-muted-foreground">
              Bienvenido{user?.email ? `, ${user.email}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={() => navigate("/admin")}>
                <Settings className="h-4 w-4" /> Admin
              </Button>
            )}
            <BackButton />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-8 px-6 py-8">
        {loading && <p className="text-sm text-muted-foreground">Cargando datos…</p>}

        {!loading && !isAdmin && (
          <section>
            <h2 className="mb-4 text-lg font-semibold text-primary">Mi actividad</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Hoy</CardDescription>
                  <CardTitle className="text-3xl">{formatNumber(metrics?.today.processed ?? 0)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground">documentos procesados</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Esta semana</CardDescription>
                  <CardTitle className="text-3xl">{formatNumber(metrics?.week.processed ?? 0)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground">documentos procesados</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Este mes</CardDescription>
                  <CardTitle className="text-3xl">{formatNumber(metrics?.month.processed ?? 0)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground">documentos procesados</p>
                </CardContent>
              </Card>
            </div>
          </section>
        )}

        {isAdmin && (
          <>
            <section>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Coins className="h-5 w-5 text-primary" />
                  <h2 className="text-lg font-semibold text-primary">Documentos y coste por empresa</h2>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={organizationId} onValueChange={(v) => setOrganizationId(v)}>
                    <SelectTrigger className="w-[220px]">
                      <SelectValue placeholder="Todas las asesorías" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las asesorías</SelectItem>
                      {organizations.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={period} onValueChange={(v) => setPeriod(v as CostPeriod)}>
                    <SelectTrigger className="w-[220px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Acumulado total</SelectItem>
                      {months.map((mk) => (
                        <SelectItem key={mk} value={mk} className="capitalize">
                          {monthLabel(mk)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {loading ? (
                <p className="text-sm text-muted-foreground">Cargando datos…</p>
              ) : report ? (
                <CompanyCostTable report={report} />
              ) : (
                <p className="text-sm text-muted-foreground">No se pudieron cargar los datos del periodo.</p>
              )}
            </section>

            {!loading && userReport && (
              <section>
                <div className="mb-4 flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  <h2 className="text-lg font-semibold text-primary">Documentos tramitados por usuario</h2>
                </div>
                <UserActivityTable rows={userReport.rows} total={userReport.total} />
              </section>
            )}

            <DevelopmentCostsCard onChanged={loadReport} />
          </>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
